import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { forkJoin, map, switchMap } from 'rxjs';
import { LearningResourceAchievement } from 'src/app/models/learning_resource_achievement.model';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  CompleteActiveEnrollmentPromptDialogComponent,
  CompleteActiveEnrollmentPromptDialogResult,
} from 'src/app/components/main/employees/rrhh/complete-active-enrollment-prompt-dialog/complete-active-enrollment-prompt-dialog.component';
import { NewLearningResourceRequestDialogComponent } from 'src/app/components/main/employees/top-menu-employee/new-learning-resource-request-dialog/new-learning-resource-request-dialog.component';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { LearningResource, LearningResourceSerialized } from 'src/app/models/learning_resource.model';
import { isActiveLearningResourceEnrollment, LearningResourceEnrollment } from 'src/app/models/learning_resource_enrollment.model';
import { User } from 'src/app/models/user.model';
import { ApiService, UtilsService } from 'src/app/services';

@Component({
  selector: 'app-upskilling-catalog-tab',
  templateUrl: './upskilling-catalog-tab.component.html',
  styleUrl: './upskilling-catalog-tab.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatTableModule,
    MatTooltipModule,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
  ],
})
export class UpskillingCatalogTabComponent extends IndexTemplateComponent implements OnInit {
  private readonly api_service = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly mat_dialog = inject(MatDialog);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly snack_bar = inject(MatSnackBar);
  protected readonly form_builder = inject(UntypedFormBuilder);
  protected readonly router = inject(Router);
  protected readonly activated_route = inject(ActivatedRoute);
  protected readonly utils_service = inject(UtilsService);

  readonly current_user = signal<User | null>(null);
  readonly active_enrollments = signal<LearningResourceEnrollment[]>([]);
  readonly active_learning_resource_ids = signal<ReadonlySet<number>>(new Set());
  readonly activating_learning_resource_id = signal<number | null>(null);
  readonly completing_learning_resource_id = signal<number | null>(null);
  readonly completed_learning_resource_ids = signal<ReadonlySet<number>>(new Set());
  readonly completed_achievements_by_resource_id = signal<Map<number, LearningResourceAchievement>>(new Map());

  dataSource = new MatTableDataSource<LearningResourceSerialized>([]);

  displayedColumns: string[] = ['name', 'duration_in_hours', 'skills', 'actions'];

  override filterFormExtraParams: Record<string, unknown> = {
    search: [''],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.initCurrentUserAndActiveEnrollment();
    this.listenSearchFormControl();
  }

  openRequestDialog(): void {
    const dialog_ref = this.mat_dialog.open(NewLearningResourceRequestDialogComponent, {
      width: '520px',
      minWidth: '400px',
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        // Sin cambios en matrícula activa; no hace falta refrescar el catálogo.
      });
  }

  openActivateDialog(learning_resource: LearningResourceSerialized): void {
    const user = this.current_user();

    if (!user) {
      this.snack_bar.open('No se pudo cargar tu usuario. Inténtalo de nuevo.', undefined, { duration: 4000 });
      return;
    }

    if (learning_resource.has_completed_achievement) {
      this.snack_bar.open('Ya completaste este curso. No puedes volver a activarlo.', undefined, { duration: 5000 });

      return;
    }

    const other_active_enrollments = this.active_enrollments().filter(
      enrollment => isActiveLearningResourceEnrollment(enrollment) && enrollment.learning_resource_id !== learning_resource.id
    );

    if (other_active_enrollments.length === 0) {
      this.submitActivation(user.id, learning_resource.id, { action: 'activate_only' });
      return;
    }

    const prompt_ref = this.mat_dialog.open(CompleteActiveEnrollmentPromptDialogComponent, {
      width: '560px',
      minWidth: '400px',
      data: {
        active_enrollments: other_active_enrollments,
        new_learning_resource: learning_resource,
      },
    });

    prompt_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((result: CompleteActiveEnrollmentPromptDialogResult | undefined): void => {
        if (result === undefined) {
          return;
        }

        this.submitActivation(user.id, learning_resource.id, result);
      });
  }

  completeActiveEnrollment(learning_resource: LearningResourceSerialized): void {
    const user = this.current_user();
    const active_enrollment_id = learning_resource.active_enrollment_id;

    if (!user) {
      this.snack_bar.open('No se pudo cargar tu usuario. Inténtalo de nuevo.', undefined, { duration: 4000 });
      return;
    }

    if (active_enrollment_id == null) {
      return;
    }

    if (!confirm(`¿Marcar "${learning_resource.name}" como curso completado? Se cerrará esta matrícula activa.`)) {
      return;
    }

    this.completing_learning_resource_id.set(learning_resource.id);

    this.api_service
      .completeActiveLearningResourceEnrollment({
        user_id: user.id,
        learning_resource_enrollment_id: active_enrollment_id,
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.completing_learning_resource_id.set(null);
          this.snack_bar.open('Curso marcado como completado', undefined, { duration: 3000 });
          this.refreshActiveEnrollmentAndCatalog();
        },
        error: (err: { error?: { error?: string } }): void => {
          this.completing_learning_resource_id.set(null);
          const message = err?.error?.error ?? 'Error al marcar el curso como completado.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
          this.change_detector_ref.markForCheck();
        },
      });
  }

  openLearningResource(learning_resource: LearningResourceSerialized): void {
    window.open(learning_resource.url!, '_blank');
  }

  private submitActivation(user_id: number, learning_resource_id: number, result: CompleteActiveEnrollmentPromptDialogResult): void {
    this.activating_learning_resource_id.set(learning_resource_id);

    const body: {
      user_id: number;
      learning_resource_id: number;
      close_enrollment_ids?: number[];
      complete_enrollment_ids?: number[];
    } = {
      user_id,
      learning_resource_id,
    };

    if (result.action === 'close_selected') {
      body.close_enrollment_ids = result.enrollment_ids;
    } else if (result.action === 'complete_selected') {
      body.complete_enrollment_ids = result.enrollment_ids;
    }

    this.api_service
      .activateLearningResourceEnrollment(body)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.activating_learning_resource_id.set(null);
          this.snack_bar.open('Formación activada', undefined, { duration: 3000 });
          this.refreshActiveEnrollmentAndCatalog();
        },
        error: (err: { error?: { error?: string } }): void => {
          this.activating_learning_resource_id.set(null);
          const message = err?.error?.error ?? 'Error al activar la formación.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
          this.change_detector_ref.markForCheck();
        },
      });
  }

  override fetchData(): void {
    const search_term = this.filterForm.get('search')?.value || '';

    const params: Record<string, unknown> = {
      page: this.filterForm.get('page')?.value ?? 1,
      per_page: this.filterForm.get('per_page')?.value ?? this.pageSize,
      order_by: 'name:asc',
      with: 'skills,learning_resource_platform',
    };

    if (search_term) {
      params.search = search_term;
    }

    this.api_service
      .getLearningResourcesPaged(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<LearningResource>): void => {
          this.dataSource.data = this.serializeLearningResources(response.data);
          this.setMetadata(response.meta.total, response.meta.current_page - 1, response.meta.per_page);
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private initCurrentUserAndActiveEnrollment(): void {
    this.api_service
      .me()
      .pipe(
        switchMap(user =>
          forkJoin({
            active_enrollments: this.api_service.getActiveLearningResourceEnrollmentsForUser(user.id),
            achievements: this.api_service.getUserLearningResourceAchievements(user.id),
          }).pipe(map(({ active_enrollments, achievements }) => ({ user, active_enrollments, achievements })))
        ),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe({
        next: ({ user, active_enrollments, achievements }): void => {
          this.current_user.set(user);
          this.setActiveEnrollments(active_enrollments);
          this.setCompletedLearningResourceIds(achievements);
          this.dataSource.data = this.serializeLearningResources(this.dataSource.data);
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private refreshActiveEnrollmentAndCatalog(): void {
    const user = this.current_user();

    if (!user) {
      return;
    }

    forkJoin({
      active_enrollments: this.api_service.getActiveLearningResourceEnrollmentsForUser(user.id),
      achievements: this.api_service.getUserLearningResourceAchievements(user.id),
    })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: ({ active_enrollments, achievements }): void => {
          this.setActiveEnrollments(active_enrollments);
          this.setCompletedLearningResourceIds(achievements);
          this.dataSource.data = this.serializeLearningResources(this.dataSource.data);
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private setActiveEnrollments(active_enrollments: LearningResourceEnrollment[]): void {
    const active = active_enrollments.filter(isActiveLearningResourceEnrollment);

    this.active_enrollments.set(active);
    this.active_learning_resource_ids.set(new Set(active.map(enrollment => enrollment.learning_resource_id)));
  }

  private setCompletedLearningResourceIds(achievements: LearningResourceAchievement[]): void {
    const completed_learning_resource_ids = new Set(achievements.map(achievement => achievement.learning_resource_id));
    const completed_achievements_by_resource_id = new Map(achievements.map(achievement => [achievement.learning_resource_id, achievement]));

    this.completed_learning_resource_ids.set(completed_learning_resource_ids);
    this.completed_achievements_by_resource_id.set(completed_achievements_by_resource_id);
  }

  private listenSearchFormControl(): void {
    this.filterForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.destroy_ref))
      .subscribe((search_term: string): void => {
        this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
        this.fetchData();
      });
  }

  private serializeLearningResources(learning_resources: LearningResource[]): LearningResourceSerialized[] {
    const active_learning_resource_ids = this.active_learning_resource_ids();
    const completed_learning_resource_ids = this.completed_learning_resource_ids();
    const active_enrollment_id_by_resource_id = new Map(
      this.active_enrollments().map(enrollment => [enrollment.learning_resource_id, enrollment.id])
    );
    const completed_achievements_by_resource_id = this.completed_achievements_by_resource_id();

    return learning_resources.map(learning_resource => {
      const completed_achievement = completed_achievements_by_resource_id.get(learning_resource.id);
      const completed_ago_label = completed_achievement
        ? this.utils_service.formatCompletedAgo(completed_achievement.end_date ?? completed_achievement.start_date)
        : null;

      return {
        ...learning_resource,
        skills_label: learning_resource.skills?.map(skill => skill.name).join(', ') || '-',
        is_active_enrollment: active_learning_resource_ids.has(learning_resource.id),
        has_completed_achievement: completed_learning_resource_ids.has(learning_resource.id),
        active_enrollment_id: active_enrollment_id_by_resource_id.get(learning_resource.id) ?? null,
        completed_ago_label: completed_ago_label === '—' ? null : completed_ago_label,
      };
    });
  }
}
